import { Link } from 'react-router-dom';
import {
  AsyncBoundary, Card, Button, Notice, useToast,
} from '@kollez/design-system';
import { useMyConsents, useUpsertConsent, type ConsentRow } from '@/lib/queries';

/**
 * /settings/privacy/consents (DPDP §6, Page 65/N).
 *
 * DPDP §6 requires informed, purpose-limited consent for every use of
 * personal data. Users must be able to view every consent they've
 * granted and revoke any of them at any time. This is that surface.
 *
 * The audit fork (Page 62/N) flagged as LEGAL priority — Consent
 * model + API existed (GET /dpdp/consents, POST upsert), no page
 * consumed them.
 */

// Human-readable descriptions of each purpose canonical string.
// Purposes not in this map still render (with the raw slug); this table
// just makes the known ones read as plain English.
const PURPOSE_LABEL: Record<string, { title: string; sub: string; revocable: boolean }> = {
  terms: {
    title: 'Terms of Service',
    sub: 'Your account only works if you agree. Revoking here effectively closes your account — use the Delete flow on Privacy instead.',
    revocable: false,
  },
  dpdp: {
    title: 'DPDP consent',
    sub: 'Under the DPDP Act, you consented at signup to the processing described in our privacy notice.',
    revocable: false,
  },
  placement_visibility: {
    title: 'Placement visibility',
    sub: 'Companies you apply to see your profile. If you revoke, you can\'t apply to campus drives.',
    revocable: true,
  },
  analytics: {
    title: 'Aggregate analytics',
    sub: 'Anonymized placement stats (branch, batch, CTC bands). Never used to re-identify you.',
    revocable: true,
  },
  ai_processing: {
    title: 'Nexie AI',
    sub: 'Lets Nexie read your profile to answer questions, suggest drives, and grade practice interviews.',
    revocable: true,
  },
  whatsapp_alerts: {
    title: 'WhatsApp reminders',
    sub: 'Interview reminders + offer nudges via WhatsApp. Turn off to receive email only.',
    revocable: true,
  },
  partner_share: {
    title: 'Share with sign-up partner',
    sub: 'The company you interviewed with gets your resume + graded attempt. Required to accept their offer.',
    revocable: true,
  },
};

function prettyPurpose(slug: string): string {
  return PURPOSE_LABEL[slug]?.title ?? slug.replace(/_/g, ' ');
}

function purposeSub(slug: string): string {
  return PURPOSE_LABEL[slug]?.sub ?? 'Consent recorded at signup.';
}

function purposeRevocable(slug: string): boolean {
  return PURPOSE_LABEL[slug]?.revocable ?? true;
}

export function ConsentsPage() {
  const list = useMyConsents();
  const upsert = useUpsertConsent();
  const toast = useToast();

  const rows: ConsentRow[] = list.data?.consents ?? [];

  async function toggle(c: ConsentRow) {
    try {
      await upsert.mutateAsync({
        purpose: c.purpose,
        granted: !c.granted,
        version: c.version,
      });
      toast.show({
        message: c.granted
          ? `Revoked: ${prettyPurpose(c.purpose)}.`
          : `Granted: ${prettyPurpose(c.purpose)}.`,
      });
    } catch (e) {
      toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Could not update consent.' });
    }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">
            <Link to="/settings/privacy" className="link">← Privacy</Link>
          </p>
          <h1 className="h-page">Manage consents</h1>
          <p className="subhead">
            Every consent you've granted is listed here. Under DPDP §6 you can revoke any of them
            at any time — the change takes effect immediately.
          </p>
        </div>
      </div>

      <Notice tone="info">
        <b>Some consents can't be revoked here.</b> Your Terms of Service and DPDP consent are
        preconditions of an account; to remove them, use <Link to="/settings/privacy" className="link">Delete my account</Link>{' '}
        which schedules a 60-day cancellable removal.
      </Notice>

      <AsyncBoundary
        kind="cards"
        loading={list.isLoading}
        error={list.error}
        empty={!list.isLoading && rows.length === 0}
        emptyTitle="No consents recorded"
        emptyHint="This is unusual — every account grants at least the Terms + DPDP consent at signup. If you're seeing this, something is off; file a grievance."
        onRetry={() => void list.refetch()}
      >
        <div className="grid gap-3">
          {rows.map((c) => {
            const revocable = purposeRevocable(c.purpose);
            const canToggle = revocable && (c.granted || c.revokedAt);
            return (
              <Card key={c._id} padding="lg">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <div className="cell-title">{prettyPurpose(c.purpose)}</div>
                      <span className={`badge ${c.granted ? 'badge-success' : ''}`}>
                        {c.granted ? 'Granted' : 'Revoked'}
                      </span>
                      {c.version && <span className="cell-sub">v{c.version}</span>}
                    </div>
                    <p className="cell-sub m-0 mt-1">{purposeSub(c.purpose)}</p>
                    <div className="cell-sub mt-2">
                      {c.grantedAt && `Granted ${new Date(c.grantedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                      {c.revokedAt && ` · Revoked ${new Date(c.revokedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                    </div>
                  </div>
                  {canToggle ? (
                    <Button
                      variant={c.granted ? 'secondary' : 'primary'}
                      loading={upsert.isPending}
                      onClick={() => toggle(c)}
                    >
                      {c.granted ? 'Revoke' : 'Grant again'}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      disabled
                      title={
                        !revocable
                          ? 'Precondition of your account — cannot be revoked from this page.'
                          : 'Not currently active.'
                      }
                    >
                      Locked
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </AsyncBoundary>

      <Notice tone="info">
        <b>Where consents come from.</b> Each is created when you first tick a box on signup, when
        you first tap Apply on a drive, or when you first ask Nexie a question. Kollez records the
        version of the notice you agreed to, so if that notice changes we'll ask you again.
      </Notice>
    </div>
  );
}
